'use client'
import { type FC, useState } from 'react'
import { Flex, Heading, Text, VStack } from '@chakra-ui/react'
import { GrandLink, GrandSection } from 'ui'
import { MagicLinkForm, type MagicLinkFormType } from 'forms'
import fetchJson, { FetchError } from 'api/utils/next/fetchJson'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import type { OverridesContextType } from 'ui/lib/overrides'
import { OverridesContextProvider, overrideText } from 'ui/lib/overrides'
import { UseSearchParamsSuspense } from 'ui/wrappers'
import { useUser } from '../../index'

interface NewPasswordPageConfig {
  paths: {
    resendMaginLinkApiRoute: string
  }
  redirects: {
    onLoggedIn: string
    toLogin: string
  }
  overrides?: OverridesContextType
}

export interface InterfaceNewPasswordPageStdOverrides {
  toLogin?: string
  requestPwd?: string
  newPasswordPageSubtitle?: string
  newPasswordPageSuccess?: string
}

interface NewPasswordPageStdProps {
  config: NewPasswordPageConfig
}

const NewPasswordPageStd: FC<NewPasswordPageStdProps> = ({ config }) => {
  useUser({ redirectTo: config.redirects.onLoggedIn, redirectIfFound: true })
  const [errorMsg, setErrorMsg] = useState('')
  const [sent, setSent] = useState(false)
  const overrides = useOverridesContext<keyof InterfaceNewPasswordPageStdOverrides>()

  const title = overrideText('Begär nytt lösenord', overrides?.requestPwd)
  const subtitle = overrideText(
    'Ange din e-postadress nedan så skickar vi en återställningslänk till dig.',
    overrides?.newPasswordPageSubtitle
  )
  const successMsg = overrideText(
    'Om e-postadressen finns hos oss skickas en återställningslänk till den.',
    overrides?.newPasswordPageSuccess
  )
  const toLoginLabel = overrideText('Tillbaka till inloggningen', overrides?.toLogin)

  const handleMagicLink = async (data: MagicLinkFormType) => {
    try {
      await fetchJson(config.paths.resendMaginLinkApiRoute, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email })
      })
      setErrorMsg('')
      setSent(true)
    } catch (error) {
      if (error instanceof FetchError) {
        setErrorMsg(error.data.message)
      } else {
        console.error('An unexpected error happened:', error)
      }
    }
  }

  return (
    <OverridesContextProvider overrides={{ ...config.overrides }}>
      <GrandSection sectionId="nytt-losenord">
        <Flex justify="center" w="full">
          <VStack
            align="stretch"
            bg="background.default"
            borderColor="gray.100"
            borderRadius="md"
            borderWidth={1}
            maxW="md"
            p={{ base: 6, md: 8 }}
            spacing={6}
            w="full"
          >
            <VStack align="stretch" spacing={2}>
              <Heading as="h1" fontSize={{ base: '2xl', md: '3xl' }}>
                {title}
              </Heading>
              <Text color="text.mild" fontSize="sm">
                {subtitle}
              </Text>
            </VStack>

            {sent ? (
              <Text color="status.success" fontSize="sm">
                {successMsg}
              </Text>
            ) : (
              <UseSearchParamsSuspense
                fallback={<MagicLinkForm error={errorMsg} onSubmit={handleMagicLink} />}
              >
                {(searchParams) => (
                  <MagicLinkForm
                    defaultValues={{ email: searchParams?.get('email') || '' }}
                    error={errorMsg}
                    onSubmit={handleMagicLink}
                  />
                )}
              </UseSearchParamsSuspense>
            )}

            <GrandLink
              alignSelf="center"
              color="primary"
              fontSize="sm"
              href={config.redirects.toLogin}
            >
              {toLoginLabel}
            </GrandLink>
          </VStack>
        </Flex>
      </GrandSection>
    </OverridesContextProvider>
  )
}

export default NewPasswordPageStd
